import { useState } from "react";
import { Link } from "react-router-dom";
import { cruises } from "@/data/cruises";
import { AnimatedSection } from "@/components/common/AnimatedSection";
import { QuoteRequestDialog } from "@/components/common/QuoteRequestDialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Ship, Anchor, ArrowRight, Star } from "lucide-react";

export const CruiseSection = () => {
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [selectedCruise, setSelectedCruise] = useState(null);
  const featuredCruises = cruises.slice(0, 2);

  const handleQuote = (cruise) => {
    setSelectedCruise({
      type: "cruise",
      cruiseName: cruise.title,
      ship: cruise.ship,
      duration: cruise.duration,
    });
    setQuoteDialogOpen(true);
  };

  return (
    <section className="bg-surface-subtle section-padding" data-testid="cruise-section">
      <div className="container-wide px-4 sm:px-6 lg:px-8">
        <AnimatedSection>
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="overline mb-3">Set Sail</p>
              <h2 className="heading-lg text-primary">Cruise Packages</h2>
            </div>
            <Link
              to="/cruises"
              className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-accent hover:text-primary transition-colors"
              data-testid="cruises-view-all"
            >
              View all cruises <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </AnimatedSection>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6 items-stretch">
          {featuredCruises.map((cruise, i) => (
            <AnimatedSection key={cruise.id} delay={i * 0.12} className="h-full">
              <div
                className="group bg-white rounded-xl shadow-card hover:shadow-card-hover transition-shadow duration-200 overflow-hidden h-full"
                data-testid={`cruise-card-${i + 1}`}
              >
                <div className="flex flex-col sm:flex-row h-full">
                  {/* Image */}
                  <div className="relative overflow-hidden sm:w-2/5 aspect-[4/3] sm:aspect-auto sm:min-h-[220px]">
                    <img
                      src={cruise.image}
                      alt={cruise.title}
                      className="w-full h-full object-cover transition-transform duration-700 ease-[cubic-bezier(0.2,0,0,1)] group-hover:scale-105"
                      loading="lazy"
                    />
                    <Badge variant="accent" className="absolute top-3 left-3">
                      {cruise.duration}
                    </Badge>
                  </div>

                  {/* Content */}
                  <div className="p-5 sm:w-3/5 flex flex-col">
                    <div className="flex items-center gap-2 mb-2">
                      <Ship className="h-4 w-4 text-accent shrink-0" />
                      <span className="text-xs text-muted-foreground font-medium">{cruise.ship}</span>
                    </div>
                    <h3 className="text-base font-bold text-primary mb-2 leading-snug group-hover:text-accent transition-colors">
                      {cruise.title}
                    </h3>
                    <div className="flex items-center gap-1.5 mb-3">
                      <Star className="h-3.5 w-3.5 fill-accent text-accent" />
                      <span className="text-xs font-semibold text-primary">{cruise.rating}</span>
                      <span className="text-xs text-muted-foreground">({cruise.reviews} reviews)</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {cruise.ports.slice(0, 4).map((port) => (
                        <span key={port} className="text-xs bg-surface-subtle text-muted-foreground px-2.5 py-1 rounded-pill flex items-center gap-1">
                          <Anchor className="h-3 w-3" /> {port}
                        </span>
                      ))}
                      {cruise.ports.length > 4 && (
                        <span className="text-xs text-muted-foreground px-2 py-1">
                          +{cruise.ports.length - 4} more
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-auto pt-3.5 border-t border-border/60">
                      <div>
                        <p className="text-xs text-muted-foreground">From</p>
                        <p className="text-xl font-bold text-primary">${cruise.startingPrice.toLocaleString()}</p>
                      </div>
                      <Button size="sm" onClick={() => handleQuote(cruise)} className="bg-accent text-white hover:bg-accent/90">
                        Get Quote <ArrowRight className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>

        <Link
          to="/cruises"
          className="sm:hidden flex items-center justify-center gap-2 text-sm font-semibold text-accent mt-6 hover:text-primary transition-colors"
          data-testid="cruises-view-all-mobile"
        >
          View All Cruises <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      <QuoteRequestDialog open={quoteDialogOpen} onOpenChange={setQuoteDialogOpen} quoteData={selectedCruise} />
    </section>
  );
};
